/* global localStorage, fetch */
let sodium: any = null;

const CLIENT_KEY_PAIR_KEY = "jc_word_client_key_pair";
const SERVER_KEY_CACHE_PREFIX = "jc_word_server_pkey:";

// In-memory cache for the server public key. Resets on reload so a backend
// redeploy / key rotation is picked up next page load, but a single session
// only fetches the key once.
const inMemoryServerKeyCache: Record<string, string> = {};

export const invalidateServerPublicKeyCache = (baseUrl?: string) => {
  if (baseUrl) {
    const normalized = baseUrl.replace(/\/$/, "");
    delete inMemoryServerKeyCache[normalized];
  } else {
    Object.keys(inMemoryServerKeyCache).forEach((k) => delete inMemoryServerKeyCache[k]);
  }
};

const ensureSodium = async () => {
  if (sodium) {
    return sodium;
  }
  const libsodium = await import("libsodium-wrappers");
  await libsodium.default.ready;
  sodium = libsodium.default;
  return sodium;
};

type KeyPair = { publicKey: string; privateKey: string };

const getOrCreateClientKeyPair = async (): Promise<KeyPair> => {
  const s = await ensureSodium();
  const cached = localStorage.getItem(CLIENT_KEY_PAIR_KEY);
  if (cached) {
    try {
      const parsed = JSON.parse(cached) as KeyPair;
      if (parsed.publicKey && parsed.privateKey) {
        return parsed;
      }
    } catch {
      // ignore and regenerate
    }
  }

  const keyPair = s.crypto_box_keypair();
  const created: KeyPair = {
    publicKey: s.to_base64(keyPair.publicKey),
    privateKey: s.to_base64(keyPair.privateKey),
  };
  localStorage.setItem(CLIENT_KEY_PAIR_KEY, JSON.stringify(created));
  return created;
};

export const getServerPublicKey = async (baseUrl: string): Promise<string> => {
  const normalizedBaseUrl = baseUrl.replace(/\/$/, "");

  // Drop any localStorage cache from older builds — we no longer use it.
  localStorage.removeItem(`${SERVER_KEY_CACHE_PREFIX}${normalizedBaseUrl}`);

  const cached = inMemoryServerKeyCache[normalizedBaseUrl];
  if (cached) {
    return cached;
  }

  const candidates: string[] = [`${normalizedBaseUrl}/api/v1/app/encryption/public-key`];

  const fetchOnce = async (endpoint: string): Promise<string | null> => {
    const response = await fetch(endpoint);
    if (!response.ok) {
      throw new Error(String(response.status));
    }
    const result = await response.json();
    const publicKey = result?.data?.publicKey;
    return publicKey || null;
  };

  let lastStatus = "";
  for (const endpoint of candidates) {
    // One retry per endpoint to absorb transient 502/504 from the cloudflare tunnel.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const publicKey = await fetchOnce(endpoint);
        if (publicKey) {
          inMemoryServerKeyCache[normalizedBaseUrl] = publicKey;
          // eslint-disable-next-line no-console
          console.log("[encryption] fetched server public key from", endpoint, ":", publicKey.slice(0, 12) + "...");
          return publicKey;
        }
        break; // 200 with no key — don't retry, try fallback endpoint
      } catch (err: any) {
        lastStatus = err?.message || "fetch failed";
        if (attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, 600));
        }
      }
    }
  }

  throw new Error(`Failed to fetch server public key${lastStatus ? ` (${lastStatus})` : ""}.`);
};

export const encryptPayload = async (data: unknown, serverPublicKey: string) => {
  const s = await ensureSodium();
  const keyPair = await getOrCreateClientKeyPair();
  const nonce = s.randombytes_buf(s.crypto_box_NONCEBYTES);
  const encrypted = s.crypto_box_easy(
    JSON.stringify(data),
    nonce,
    s.from_base64(serverPublicKey),
    s.from_base64(keyPair.privateKey)
  );
  return {
    envelope: {
      encrypted: s.to_base64(encrypted),
      nonce: s.to_base64(nonce),
      clientPublicKey: keyPair.publicKey,
    },
    clientPublicKey: keyPair.publicKey,
  };
};

export const decryptPayload = async (encrypted: string, nonce: string, serverPublicKey: string) => {
  const s = await ensureSodium();
  const keyPair = await getOrCreateClientKeyPair();
  const decrypted = s.crypto_box_open_easy(
    s.from_base64(encrypted),
    s.from_base64(nonce),
    s.from_base64(serverPublicKey),
    s.from_base64(keyPair.privateKey)
  );
  return JSON.parse(s.to_string(decrypted));
};

export const normalizeEncryptedResponseData = async (responseData: any) => {
  const encryptedBlob = responseData?.data;
  if (!encryptedBlob?.encrypted || !encryptedBlob?.nonce || !encryptedBlob?.serverPublicKey) {
    return responseData;
  }
  const decrypted = await decryptPayload(
    encryptedBlob.encrypted,
    encryptedBlob.nonce,
    encryptedBlob.serverPublicKey
  );
  return {
    ...responseData,
    data: decrypted,
  };
};
